import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { createAppApi, type AppApi } from '../api/client';

/**
 * Один экземпляр API на всё приложение.
 *
 * Контекст, а не модульный синглтон: синглтон читал бы `initData` в момент импорта, то есть
 * до того, как SDK её восстановит, и первый защищённый запрос уходил бы без подписи.
 * Здесь `getInitData` — функция, и значение берётся в момент запроса.
 *
 * ТОЧКА РАСШИРЕНИЯ: кабинет бизнеса получает свой API из этого же контекста
 * (`useAppApi().transport`), а не заводит второй клиент — заголовок авторизации, разбор
 * ошибок и обработка сети обязаны быть одни на приложение.
 */
const ApiContext = createContext<AppApi | null>(null);

/** База API. В dev её проксирует Vite на бэкенд, в проде оба сервиса стоят за одним Nginx. */
const API_BASE_URL = '/api';

export function ApiProvider(props: {
  children: ReactNode;
  getInitData: () => string | null;
}): JSX.Element {
  const { getInitData } = props;
  const api = useMemo(() => createAppApi({ baseUrl: API_BASE_URL, getInitData }), [getInitData]);
  return <ApiContext.Provider value={api}>{props.children}</ApiContext.Provider>;
}

export function useAppApi(): AppApi {
  const api = useContext(ApiContext);
  if (!api) throw new Error('useAppApi вызван вне <ApiProvider>');
  return api;
}
